import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import { Note } from "@/components/ui/Note";
import { Panel } from "@/components/ui/Panel";
import { ToastSurface } from "@/components/ui/ToastSurface";
import { useAuth } from "@/contexts/AuthContext";
import { clearAccessDenied, type AccessDenial } from "@/lib/access-denied";
import { checkAccess } from "@/services/access";
import { ApiError } from "@/services/api-client";

/** Where the last Try again left things; a yes leaves the screen, so it has no state here. */
type Recheck = "idle" | "checking" | "still-refused" | "unanswered";

/**
 * Asks the API again whether this account may use the application.
 *
 * Only the API's yes lifts the screen. A refusal and a failed check both leave
 * it up, so an error while checking can never let anyone in.
 */
function useAccessRecheck() {
  const [recheck, setRecheck] = useState<Recheck>("idle");

  const tryAgain = useCallback(() => {
    setRecheck("checking");
    void (async () => {
      try {
        await checkAccess();
      } catch (failure) {
        setRecheck(
          failure instanceof ApiError && failure.status === 403
            ? "still-refused"
            : "unanswered",
        );
        return;
      }
      clearAccessDenied();
    })();
  }, []);

  return { recheck, tryAgain };
}

function RecheckNote({ recheck }: { recheck: Recheck }) {
  if (recheck === "still-refused") {
    return (
      <Note role="status">
        We checked again, and this account still does not have access.
      </Note>
    );
  }
  if (recheck === "unanswered") {
    return (
      <Note tone="destructive" role="alert">
        We could not check just now. Try again in a moment.
      </Note>
    );
  }
  return null;
}

/**
 * Shown when the sign-in worked but this account cannot use the pilot: either
 * it is not admitted, or its address is not verified yet.
 *
 * Signing in successfully and being allowed to use the application are
 * different things, and the gap between them is confusing enough without an
 * application that half loads. Access is granted rather than requested during
 * the pilot, so the honest thing to say is that the account is not on the
 * list, not that something went wrong.
 *
 * Somebody often lands here first and is given a seat afterwards, so Try again
 * asks the API afresh rather than sending them to sign out and back in. The
 * session holds no admission, which is why the next answer is current.
 *
 * An unverified address gets its own message, because a brand-new sign-up has
 * usually just not opened the verification mail, and "not on the list" would
 * send them to support for nothing. Reloading after verifying is enough: the
 * API asks the provider again on every request until it hears a yes.
 *
 * Signing out matters more than it looks: the provider holds the session, so
 * without it somebody who signed in with the wrong account of two has no way
 * back to the other one. This page is a root of its own rather than a page
 * inside the workflow, which is why it mounts its own toast surface.
 */
export function NotAuthorisedPage({ denial }: { denial: AccessDenial }) {
  const { user, logout } = useAuth();
  const { recheck, tryAgain } = useAccessRecheck();
  const unverified = denial === "email-unverified";

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <ToastSurface />
      <Panel pad="none" className="w-full max-w-md">
        <div className="flex flex-col space-y-1.5 p-4 pb-0">
          <div className="font-semibold leading-none tracking-tight">
            {unverified
              ? "Verify your email address"
              : "You do not have access"}
          </div>
          <p className="text-sm text-muted-foreground">
            {unverified
              ? "Open the verification link we sent when this account was created, then reload this page."
              : "Patent Studio is in a closed pilot, and this account is not on the list."}
          </p>
        </div>
        <div className="space-y-4 p-4">
          {user?.email && (
            <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              Signed in as <span className="font-medium">{user.email}</span>
            </p>
          )}
          {unverified ? (
            <Button className="w-full" onClick={() => window.location.reload()}>
              Reload
            </Button>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                If you should have access, ask the Patent Studio team to add
                this address. If you have another account, sign out and use that
                one.
              </p>
              <Button
                className="w-full"
                held={recheck === "checking"}
                onClick={tryAgain}
              >
                {recheck === "checking" ? "Checking..." : "Try again"}
              </Button>
              <RecheckNote recheck={recheck} />
            </>
          )}
          <Button variant="secondary" className="w-full" onClick={logout}>
            Sign out
          </Button>
        </div>
      </Panel>
    </div>
  );
}
